// src/components/ConversationListPage.js
/* eslint-disable react/jsx-no-duplicate-props */
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import PageHeader from './common/PageHeader';
import { useWebSocket } from '../contexts/WebSocketContext';
import { motion } from 'framer-motion';

import { Typography, List, ListItem, ListItemButton, Divider, CircularProgress, Box, Button, Chip, Avatar, Badge, Tabs, Tab, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const containerVariants = {
  hidden: { opacity: 1 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100 } }
};

const MotionListItem = motion.create(ListItem);

export default function ConversationListPage() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activatingAll, setActivatingAll] = useState(false);
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');

  // Function to activate bot for all conversations
  const handleActivateAllBots = async () => {
    setActivatingAll(true);
    try {
      const response = await axiosInstance.post('/chatbot/conversations/activate-all/');
      if (response.data.success) {
        // Refresh conversations list
        const refreshResponse = await axiosInstance.get('/chatbot/conversations/');
        const conversationData = refreshResponse.data.results || refreshResponse.data;
        setConversations(Array.isArray(conversationData) ? conversationData : []);
        alert(`تم تفعيل البوت لـ ${response.data.updated_count} محادثة`);
      }
    } catch (err) {
      console.error('Failed to activate all bots:', err);
      alert('فشل تفعيل البوت للمحادثات');
    } finally {
      setActivatingAll(false);
    }
  };

  // Function to activate bot for a single conversation
  const handleActivateBot = async (e, conversationId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('هل أنت متأكد من تفعيل البوت لهذه المحادثة؟')) return;

    try {
      await axiosInstance.post(`/chatbot/conversations/${conversationId}/status/`, {
        status: 'active_bot'
      });
      // Update local state without full reload
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, status: 'active_bot', is_bot_active: true } : c
      ));
    } catch (err) {
      console.error('Failed to activate bot:', err);
      alert('فشل تفعيل البوت');
    }
  };


  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/chatbot/conversations/`);

        const conversationData = response.data.results || response.data;
        setConversations(Array.isArray(conversationData) ? conversationData : []);

        // ✨ NEW: حفظ رابط الصفحة التالية
        setNextPageUrl(response.data.next);
        console.log('✅ Conversations loaded successfully');

      } catch (err) {
        console.log('⚠️ Conversations endpoint not available, using empty list');
        setConversations([]);
        setNextPageUrl(null);
        console.error("Fetch Conversations Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, []);

  // ⚡ تحديث لحظي: أي رسالة جديدة تعيد تحميل القائمة بصمت (بدون سبينر)
  const { lastMessage } = useWebSocket() || {};
  useEffect(() => {
    if (lastMessage?.type !== 'new_message') return;
    (async () => {
      try {
        const response = await axiosInstance.get(`/chatbot/conversations/`);
        const conversationData = response.data.results || response.data;
        setConversations(Array.isArray(conversationData) ? conversationData : []);
        setNextPageUrl(response.data.next);
      } catch (e) { /* silent */ }
    })();
  }, [lastMessage]);

  // دالة لجلب المزيد من المحادثات
  const handleLoadMore = async () => {
    if (!nextPageUrl) return;

    setLoadingMore(true);
    try {
      const response = await axiosInstance.get(nextPageUrl);
      const newConversations = response.data.results || response.data;

      setConversations(prev => [...prev, ...newConversations]);
      setNextPageUrl(response.data.next);

    } catch (err) {
      console.error("Failed to load more conversations:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // دالة لتنسيق التاريخ
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // دالة لتحديد لون الحالة
  // Safe status-to-color mapping (avoids corrupted non-ASCII cases)
  const statusToColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <PageHeader
        title="سجل المحادثات"
        subtitle="إدارة وتتبع جميع المحادثات مع المستخدمين"
        actions={(
          <Button
            variant="contained"
            color="success"
            startIcon={activatingAll ? <CircularProgress size={18} color="inherit" /> : <SmartToyIcon />}
            onClick={handleActivateAllBots}
            disabled={activatingAll}
            sx={{ whiteSpace: 'nowrap', '& .MuiButton-startIcon': { ml: 0.5, mr: -0.25 } }}
          >
            {activatingAll ? 'جاري التفعيل...' : 'تفعيل البوت للكل'}
          </Button>
        )}
      />

      {/* Filters Row */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        bgcolor: 'background.paper',
        p: 2,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Tabs
          className="ar-replacement"
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          sx={{ flexGrow: 1, minWidth: 0 }}
        >
          <Tab label={"\u0627\u0644\u0643\u0644"} value="all" />
          <Tab label={"\u063A\u064A\u0631 \u0645\u0642\u0631\u0648\u0621"} value="unread" />
          <Tab label={"\u0645\u0639 \u0627\u0644\u0628\u0648\u062A"} value="bot" />
          <Tab label={"\u0645\u062D\u0648\u0651\u0644"} value="human" />
          <Tab label={"\u0645\u063A\u0644\u0642"} value="closed" />
        </Tabs>

        <TextField
          className="ar-replacement"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={"\u0627\u0628\u062D\u062B \u0628\u0631\u0642\u0645 \u0627\u0644\u0647\u0627\u062A\u0641 \u0623\u0648 \u0627\u0644\u0646\u0635"}
          size="small"
          sx={{ minWidth: 280 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />
      </Box>

      <List
        component={motion.ul}
        variants={containerVariants}
        initial="hidden"
        animate="show"
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: 0,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {conversations.length > 0 ? (
          conversations
            .filter((c) => {
              if (tab === 'unread' && !(c.has_unread || (c.unread_count > 0))) return false;
              if (tab === 'bot' && c.status !== 'active_bot') return false;
              if (tab === 'human' && c.status !== 'active_human') return false;
              if (tab === 'closed' && c.status !== 'closed') return false;
              return true;
            })
            .filter((c) => {
              const q = query.trim().toLowerCase();
              if (!q) return true;
              const hay = [c.user_phone_number, c.last_message_content, c.ui_status, c.status]
                .map(x => (x || '').toString().toLowerCase())
                .join(' ');
              return hay.includes(q);
            })
            .map((convo, index) => (
              <React.Fragment key={convo.id}>
                <MotionListItem variants={itemVariants} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={`/conversations/${convo.id}`}
                    sx={{
                      py: 3,
                      px: 3,
                      bgcolor: convo.status === 'active_human' ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                      borderRight: convo.status === 'active_human' ? '4px solid #ff9800' : 'none',
                      '&:hover': {
                        bgcolor: convo.status === 'active_human' ? 'rgba(255, 152, 0, 0.15)' : 'primary.50',
                        transform: 'translateX(-4px)',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Badge color="error" overlap="circular" badgeContent={convo.unread_count} invisible={!convo.unread_count} sx={{ mr: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: convo.status === 'active_human' ? 'warning.main' : 'primary.main',
                          width: 48,
                          height: 48
                        }}
                      >
                        {convo.status === 'active_human' ? <SupportAgentIcon /> : <PersonIcon />}
                      </Avatar>
                    </Badge>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="600" noWrap>
                          {convo.user_phone_number || "\u0645\u0633\u062A\u062E\u062F\u0645 \u063A\u064A\u0631 \u0645\u0639\u0631\u0648\u0641"}
                        </Typography>
                        <Chip
                          label={convo.status || "\u063A\u064A\u0631 \u0645\u062D\u062F\u062F"}
                          size="small"
                          sx={{ fontSize: '0.75rem' }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip label={(convo.ui_status || convo.status || "\u063A\u064A\u0631 \u0645\u062D\u062F\u062F")} color={convo.ui_color || statusToColor(convo.status)} size="small" variant="outlined" />
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {convo.last_message_content || "\u0644\u0627 \u064A\u0648\u062C\u062F \u0631\u0633\u0627\u0626\u0644 \u0628\u0639\u062F."}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          {formatDate(convo.last_message_timestamp || convo.updated_at)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      {/* Single Chat Bot Toggle */}
                      {convo.status !== 'active_bot' && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="success"
                          onClick={(e) => handleActivateBot(e, convo.id)}
                          startIcon={<SmartToyIcon />}
                          sx={{ mr: 1, whiteSpace: 'nowrap', minWidth: 'auto' }}
                        >
                          تفعيل البوت
                        </Button>
                      )}

                      <ChatBubbleOutlineIcon sx={{ color: 'primary.main' }} />
                    </Box>
                  </ListItemButton>
                </MotionListItem>
                {index < conversations.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))
        ) : (
          <Box sx={{ textAlign: 'center', p: 6 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {"\u0644\u0627 \u062A\u0648\u062C\u062F \u0645\u062D\u0627\u062F\u062B\u0627\u062A"}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {"\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0623\u064A \u0645\u062D\u0627\u062F\u062B\u0627\u062A \u062D\u062A\u0649 \u0627\u0644\u0622\u0646"}
            </Typography>
          </Box>
        )}
      </List>


      {
        nextPageUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              onClick={handleLoadMore}
              disabled={loadingMore}
              variant="outlined"
              startIcon={loadingMore ? <CircularProgress size={20} /> : null}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {loadingMore ? "جاري التحميل..." : "تحميل المزيد"}
            </Button>
          </Box>
        )
      }
    </Box>
  );
}
